import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import './SupervisorUpdatePage.css';
import { toast } from '../components/Toast';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function ViewModal({ update, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});
  const [photosLoading, setPhotosLoading] = useState(false);

  useEffect(() => {
    if (!update) return;
    setPhotos([]);
    setPhotoUrls({});
    setPhotosLoading(true);
    axios.get(`/supervisor-updates/${update.id}/photos`)
      .then(res => {
        const data = res.data?.data || [];
        setPhotos(Array.isArray(data) ? data : []);
        // Fetch each image as a blob URL so auth header is sent
        (Array.isArray(data) ? data : []).forEach(p => {
          axios.get(`/supervisor-updates/${update.id}/photos/${p.id}/download`, { responseType: 'blob' })
            .then(r => {
              const url = URL.createObjectURL(r.data);
              setPhotoUrls(prev => ({ ...prev, [p.id]: url }));
            }).catch(() => {});
        });
      })
      .catch(() => {})
      .finally(() => setPhotosLoading(false));
    return () => {
      // Revoke blob URLs on unmount
      Object.values(photoUrls).forEach(u => URL.revokeObjectURL(u));
    };
  }, [update?.id]);

  if (!update) return null;
  return (
    <div className="su-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="su-modal">
        <div className="su-modal-header">
          <h3>Supervisor Update Detail</h3>
          <button className="su-close" onClick={onClose}>&times;</button>
        </div>
        <div className="su-modal-body">
          <div className="su-detail-grid">
            <div className="su-detail-item">
              <span className="su-detail-label">Project</span>
              <span className="su-detail-value"><strong>{update.projectName || '—'}</strong></span>
            </div>
            <div className="su-detail-item">
              <span className="su-detail-label">Update Date</span>
              <span className="su-detail-value">{fmt(update.updateDate)}</span>
            </div>
            <div className="su-detail-item">
              <span className="su-detail-label">Work Progress</span>
              <span className="su-detail-value">
                <div className="su-progress-bar">
                  <div className="su-progress-fill" style={{ width: `${update.workProgress}%` }} />
                </div>
                <span>{update.workProgress}%</span>
              </span>
            </div>
            <div className="su-detail-item">
              <span className="su-detail-label">Status</span>
              <span className="su-detail-value">
                <span className={`su-badge ${update.approvedByName ? 'su-badge-approved' : 'su-badge-pending'}`}>
                  {update.approvedByName ? `✓ Approved by ${update.approvedByName}` : 'Pending Approval'}
                </span>
              </span>
            </div>
          </div>
          <div className="su-detail-section">
            <span className="su-detail-label">Description</span>
            <div className="su-detail-desc">{update.description || <span className="su-muted">No description</span>}</div>
          </div>
          {update.reviewComment && (
            <div className="su-detail-section">
              <span className="su-detail-label">Reviewer Comment</span>
              <div className="su-detail-desc su-comment-existing">{update.reviewComment}</div>
            </div>
          )}
          {/* Photos */}
          <div className="su-detail-section">
            <span className="su-detail-label">Site Photos</span>
            {photosLoading ? (
              <div className="su-muted" style={{ marginTop: 8 }}>Loading photos…</div>
            ) : photos.length === 0 ? (
              <div className="su-muted" style={{ marginTop: 8 }}>No photos attached</div>
            ) : (
              <div className="su-photo-grid">
                {photos.map(p => (
                  <a key={p.id}
                    href={photoUrls[p.id] || '#'}
                    target="_blank"
                    rel="noreferrer"
                    className="su-photo-thumb"
                    title={p.fileName}
                  >
                    {photoUrls[p.id]
                      ? <img src={photoUrls[p.id]} alt={p.fileName} />
                      : <div className="su-photo-placeholder">⏳</div>
                    }
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="su-modal-footer">
          <button className="su-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function MySupervisorUpdates() {
  const navigate = useNavigate();
  const [updates, setUpdates]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);

  useEffect(() => { fetchMyUpdates(); }, []);

  const fetchMyUpdates = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/supervisor-updates/my-updates');
      const data = res.data?.data || [];
      setUpdates(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load updates');
    } finally { setLoading(false); }
  };

  const approved = updates.filter(u => u.approvedByName).length;
  const pending  = updates.filter(u => !u.approvedByName).length;

  return (
    <div className="supervisor-update-page">
      <div className="page-header">
        <div>
          <h1>My Supervisor Updates</h1>
          <p style={{ color: '#666', margin: '4px 0 0' }}>View and track your site progress reports</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/supervisor-updates/new')}>
          + New Update
        </button>
      </div>



      <div className="su-stats">
        <div className="su-stat"><span className="su-stat-val">{updates.length}</span><span className="su-stat-lbl">Total</span></div>
        <div className="su-stat"><span className="su-stat-val su-approved">{approved}</span><span className="su-stat-lbl">Approved</span></div>
        <div className="su-stat"><span className="su-stat-val su-pending">{pending}</span><span className="su-stat-lbl">Pending</span></div>
      </div>

      {loading ? (
        <div className="su-loading">Loading…</div>
      ) : updates.length === 0 ? (
        <div className="su-empty">No updates yet. <button className="su-link" onClick={() => navigate('/supervisor-updates/new')}>Create your first update</button></div>
      ) : (
        <div className="su-table-wrap">
          <table className="su-table">
            <thead>
              <tr><th>Date</th><th>Project</th><th>Progress</th><th>Description</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {updates.map(u => (
                <tr key={u.id} className="su-clickable" onClick={() => setSelected(u)}>
                  <td data-label="Date">{fmt(u.updateDate)}</td>
                  <td data-label="Project"><strong>{u.projectName || '—'}</strong></td>
                  <td data-label="Progress">
                    <div className="su-progress-inline">
                      <div className="su-progress-bar-sm">
                        <div className="su-progress-fill-sm" style={{ width: `${u.workProgress}%` }} />
                      </div>
                      <span>{u.workProgress}%</span>
                    </div>
                  </td>
                  <td data-label="Description" className="su-desc-preview">
                    {u.description ? (u.description.length > 60 ? u.description.slice(0, 60) + '…' : u.description) : '—'}
                  </td>
                  <td data-label="Status">
                    <span className={`su-badge ${u.approvedByName ? 'su-badge-approved' : 'su-badge-pending'}`}>
                      {u.approvedByName ? `✓ ${u.approvedByName}` : 'Pending'}
                    </span>
                  </td>
                  <td data-label="Actions" onClick={e => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button className="btn btn-sm btn-primary su-view-btn" onClick={() => setSelected(u)}>View</button>
                      {!u.approvedByName && (
                        <button 
                          className="btn btn-sm btn-secondary su-edit-btn" 
                          onClick={() => navigate(`/supervisor-updates/edit/${u.id}`)}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ViewModal update={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default MySupervisorUpdates;
